import { finalSurveyTabAvailable, finalSurveyWorkPhase } from '../../modules/docs/utils/surveySheetDelete';

export type SurveyKindPick = 'first' | 'final';

export function isAcceptedOrCashStatus(status?: string | null): boolean {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'accepted' || normalized === 'cash';
}

export type SurveyStatusChip = {
  label: string;
  color: 'success' | 'primary' | 'neutral' | 'warning' | 'danger';
};

/** Customer / commercial status for First Survey — distinct from Final work phase. */
export function firstSurveyStatusChip(args: {
  firstIsTransmitted: boolean;
  quoteStatus?: string | null;
}): SurveyStatusChip {
  const status = String(args.quoteStatus || '').trim().toLowerCase();
  if (status === 'cash') return { label: 'Cash', color: 'success' };
  if (status === 'accepted') return { label: 'Accepted', color: 'success' };
  if (status === 'rejected') return { label: 'Rejected', color: 'danger' };
  if (args.firstIsTransmitted || status === 'sent') return { label: 'Sent', color: 'primary' };
  return { label: 'Draft', color: 'neutral' };
}

/** Final Survey status — issued / work / customer, never the First accept chip. */
export function finalSurveyStatusChip(args: {
  workPhase: 'not_started' | 'in_progress' | 'complete';
  customerAcceptedAt?: string | null;
  reapprovalTransmissionVersionId?: string | null;
  needsReapproval?: boolean;
  classification?: string | null;
}): SurveyStatusChip {
  if (args.workPhase !== 'complete') {
    if (args.workPhase === 'in_progress') return { label: 'In progress', color: 'neutral' };
    return { label: 'Not issued', color: 'neutral' };
  }
  if (args.customerAcceptedAt) return { label: 'Customer accepted', color: 'success' };
  if (args.classification === 'no_reapproval') return { label: 'Accepted (customer’s behalf)', color: 'success' };
  if (args.reapprovalTransmissionVersionId) return { label: 'Reapproval sent', color: 'warning' };
  if (args.needsReapproval) return { label: 'Reapproval needed', color: 'warning' };
  return { label: 'Complete', color: 'success' };
}

export type CommercialFinalAcceptance = {
  mode: 'customer' | 'staff_behalf';
  date?: string | null;
} | null;

/** Quote row overlay once Final is accepted — by the customer, or on their behalf. */
export function resolveCommercialFinalAcceptance(args: {
  classification?: string | null;
  customerAcceptedAt?: string | null;
  decidedAt?: string | null;
  workPhase?: 'not_started' | 'in_progress' | 'complete' | null;
}): CommercialFinalAcceptance {
  if (args.workPhase && args.workPhase !== 'complete') return null;
  if (args.customerAcceptedAt) {
    return { mode: 'customer', date: args.customerAcceptedAt };
  }
  if (args.classification === 'no_reapproval') {
    return { mode: 'staff_behalf', date: args.decidedAt || null };
  }
  return null;
}

function isCommercialKind(kind?: string | null): boolean {
  return kind === 'Quotation' || kind === 'Estimate';
}

/** Quote/estimate row labels: First accept until Final is accepted (customer or on their behalf). */
export function commercialDocumentStatusLabel(
  kind?: string | null,
  status?: string | null,
  finalAcceptance?: CommercialFinalAcceptance,
): string {
  const normalized = String(status || '').trim().toLowerCase();
  if (!isCommercialKind(kind)) {
    return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Draft';
  }
  if ((normalized === 'accepted' || normalized === 'cash') && finalAcceptance?.mode === 'staff_behalf') {
    return 'Final Survey accepted (customer’s behalf)';
  }
  if ((normalized === 'accepted' || normalized === 'cash') && finalAcceptance?.mode === 'customer') {
    return 'Final Survey accepted';
  }
  if (normalized === 'accepted') return 'First Survey accepted';
  if (normalized === 'cash') return 'First Survey cash';
  if (normalized === 'sent') return 'First Survey sent';
  if (normalized === 'rejected') return 'First Survey rejected';
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Draft';
}

export function commercialDocumentStatusCaption(
  kind?: string | null,
  status?: string | null,
  date?: string | null,
  finalAcceptance?: CommercialFinalAcceptance,
): string | undefined {
  const normalized = String(status || '').trim().toLowerCase();
  if (isCommercialKind(kind) && (normalized === 'accepted' || normalized === 'cash') && finalAcceptance) {
    if (finalAcceptance.mode === 'staff_behalf') {
      return finalAcceptance.date
        ? `Accepted on the customer’s behalf on ${finalAcceptance.date}`
        : 'Accepted on the customer’s behalf';
    }
    return finalAcceptance.date
      ? `Final Survey accepted on ${finalAcceptance.date}`
      : 'Final Survey accepted by the customer';
  }
  if (!date) {
    if (isCommercialKind(kind) && (normalized === 'accepted' || normalized === 'cash')) {
      return 'Acceptance is First Survey';
    }
    return undefined;
  }
  if (isCommercialKind(kind)) {
    if (normalized === 'accepted') return `First Survey accepted on ${date}`;
    if (normalized === 'cash') return `First Survey cash on ${date}`;
    if (normalized === 'sent') return `First Survey sent on ${date}`;
    if (normalized === 'rejected') return `First Survey rejected on ${date}`;
  }
  if (normalized === 'accepted') return `Accepted on ${date}`;
  if (normalized === 'rejected') return `Rejected on ${date}`;
  if (normalized === 'sent') return `Sent on ${date}`;
  if (normalized === 'paid') return `Paid on ${date}`;
  if (normalized === 'cash') return `Cash on ${date}`;
  return undefined;
}

/**
 * Progress is only for a draft estimate that has never been emailed.
 * A transmitted estimate waiting for a reply is not Progress — wait for Accept/Cash
 * (auto-creates the quote) or Reject (new workspace).
 */
export function canProgressUnsentEstimate(args: {
  estimateStatus?: string | null;
  hasQuote: boolean;
  alreadyProgressed?: boolean;
  firstHasBeenTransmitted?: boolean;
}): boolean {
  if (args.hasQuote || args.alreadyProgressed || args.firstHasBeenTransmitted) return false;
  const status = String(args.estimateStatus || '').trim().toLowerCase();
  if (!status || status === 'draft') return true;
  return false;
}

export function primarySurvey<T extends { kind: string; optionId?: string | null }>(
  surveys: T[],
  kind: SurveyKindPick,
): T | null {
  const ofKind = surveys.filter((survey) => survey.kind === kind);
  return ofKind.find((survey) => !survey.optionId) || ofKind[0] || null;
}

type DatedDocument = { kind: string; status?: string | null; updatedAt?: string | null; createdAt: string };

function latestOf<T extends DatedDocument>(docs: T[]): T | null {
  return [...docs].sort((a, b) => (
    (Date.parse(b.updatedAt || b.createdAt) || 0) - (Date.parse(a.updatedAt || a.createdAt) || 0)
  ))[0] || null;
}

/** Same rule as the Quotes stage Final Survey tab, worked out from the project's documents. */
export function finalSurveyOpenForProject(args: {
  documents: DatedDocument[];
  finalSurvey: Parameters<typeof finalSurveyWorkPhase>[0];
}): boolean {
  const latestQuote = latestOf(args.documents.filter((doc) => doc.kind === 'Quotation'));
  const latestEstimate = latestOf(args.documents.filter((doc) => doc.kind === 'Estimate'));
  return finalSurveyTabAvailable({
    quoteStatus: latestQuote?.status,
    documentKind: latestQuote?.kind,
    rolledFromAcceptedEstimate: !!latestQuote && isAcceptedOrCashStatus(latestEstimate?.status),
    finalWorkStarted: finalSurveyWorkPhase(args.finalSurvey) !== 'not_started',
  });
}

export function isVisualDesignerMeta(meta: unknown) {
  return String((meta as { feature?: string } | null)?.feature || '').trim() === 'visual_designer';
}

export function excludeVisualDesignerLines<T extends { metaJson?: unknown; meta_json?: unknown }>(lines: T[]): T[] {
  return lines.filter((line) => !isVisualDesignerMeta(line.metaJson ?? line.meta_json));
}

export function draftQuoteTitle(projectName?: string | null, customerName?: string | null) {
  const raw = String(projectName || customerName || 'quotation').trim() || 'quotation';
  return raw.charAt(0).toLowerCase() + raw.slice(1);
}

export type CloneWorkspaceMode = 'copy_quote_design' | 'copy_quote' | 'copy_design';

export type CloneSourceKind = 'Quotation' | 'Estimate';

/** Clone options worded for the source workspace's commercial document. */
export function cloneWorkspaceOptions(sourceKind: CloneSourceKind = 'Quotation'): Array<{
  value: CloneWorkspaceMode;
  label: string;
  description: string;
}> {
  const noun = sourceKind === 'Estimate' ? 'Estimate' : 'Quote';
  return [
    {
      value: 'copy_quote_design',
      label: `Copy ${noun} & Design`,
      description: `Copies ${noun} Details and First Survey into a new workspace. Final Survey is not copied.`,
    },
    {
      value: 'copy_quote',
      label: `Copy ${noun} Only`,
      description: `Copies ${noun} Details only. First Survey starts empty.`,
    },
    {
      value: 'copy_design',
      label: 'Copy Design only',
      description: `Copies First Survey only. ${noun} Details start as a blank draft.`,
    },
  ];
}

export const CLONE_WORKSPACE_OPTIONS = cloneWorkspaceOptions('Quotation');

/** The document a clone copies from: the workspace quote, else its estimate (latest of each). */
export function pickCloneSourceDocument<T extends { kind: string; updatedAt?: string; createdAt?: string }>(
  docs: T[],
): T | null {
  const latestOfKind = (kind: CloneSourceKind) => [...docs]
    .filter((doc) => doc.kind === kind)
    .sort((a, b) => (Date.parse(b.updatedAt || b.createdAt || '') || 0) - (Date.parse(a.updatedAt || a.createdAt || '') || 0))[0];
  return latestOfKind('Quotation') || latestOfKind('Estimate') || null;
}

export function cloneCopyPlan(mode: CloneWorkspaceMode) {
  return {
    copyQuote: mode === 'copy_quote_design' || mode === 'copy_quote',
    copyDesign: mode === 'copy_quote_design' || mode === 'copy_design',
  };
}

